// // src/providers/TokenProvider.tsx
// import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
// import { tokenService } from '../utils/services/auth.service';
// import { TokenRequest } from '../utils/dto/request';
// import { getAuthCredentials } from '../config/authConfig';
// import { Box, Typography } from '@mui/material';

// interface TokenContextType {
//     token: string | null;
//     tokenExpiry: number | null;
//     tokenError: boolean;
//     sessionExpired: boolean;
//     setToken: (token: string | null) => void;
//     setTokenExpiry: (expiry: number | null) => void;
//     setTokenError: (error: boolean) => void;
//     setSessionExpired: (expired: boolean) => void;
// }

// const TokenContext = createContext<TokenContextType | undefined>(undefined);

// export const useToken = () => {
//     const context = useContext(TokenContext);
//     if (!context) throw new Error('useToken must be used within TokenProvider');
//     return context;
// };

// let globalSetSessionExpired: (expired: boolean) => void = () => { };

// interface TokenProviderProps {
//     children: ReactNode;
// }

// const TokenProvider = ({ children }: TokenProviderProps) => {
//     const [token, setToken] = useState<string | null>(null);
//     const [tokenExpiry, setTokenExpiry] = useState<number | null>(null);
//     const [tokenError, setTokenError] = useState(false);
//     const [sessionExpired, setSessionExpired] = useState(false);

//     useEffect(() => {
//         const fetchToken = async () => {
//             const existingToken = localStorage.getItem('auth_token');
//             const tokenExpiry = localStorage.getItem('token_expiry');
//             const now = Date.now();

//             if (existingToken && tokenExpiry && now < parseInt(tokenExpiry)) {
//                 setToken(existingToken);
//                 setTokenExpiry(parseInt(tokenExpiry));
//                 return;
//             }

//             try {
//                 const { username, password } = getAuthCredentials();
//                 const payload: TokenRequest = { username, password };
//                 const { code, data } = await tokenService(payload);
//                 if (code  === 200 && data?.access_token) {
//                     const newToken = `Bearer ${data.access_token}`;
//                     const expiry = now + 3600 * 1000; // 60 minutes
//                     localStorage.setItem('auth_token', newToken);
//                     localStorage.setItem('token_expiry', expiry.toString());
//                     setToken(newToken);
//                     setTokenExpiry(expiry);
//                     setTokenError(false);
//                     setSessionExpired(false);
//                 } else {
//                     setTokenError(true);
//                 }
//             } catch (error) {
//                 console.error('Failed to fetch token:', error);
//                 setTokenError(true);
//             }
//         };

//         fetchToken();
//     }, []);

//     useEffect(() => {
//         globalSetSessionExpired = setSessionExpired;
//     }, []);

//     if (tokenError) {
//         return (
//             <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
//                 <Typography variant="h6" color="error">
//                     Unable to connect. Please try again later.
//                 </Typography>
//             </Box>
//         );
//     }

//     return (
//         <TokenContext.Provider
//             value={{ token, tokenExpiry, tokenError, sessionExpired, setToken, setTokenExpiry, setTokenError, setSessionExpired }}
//         >
//             {children}
//         </TokenContext.Provider>
//     );
// };

// export default TokenProvider;
// export { globalSetSessionExpired };

// src/providers/TokenProvider.tsx
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import axios from 'axios'; // Use plain axios for tokenService
import { TokenRequest } from '../utils/dto/request';
import { getAuthCredentials } from '../config/authConfig';
import { Box, Button, Typography } from '@mui/material';
import ClientsAxios from '../utils/client-axios';
import { tokenService } from '../utils/services/chat.service';
import LoaderText from '../components/loader/LoaderText';

const { THIRD_PARTY_API_SERVER } = ClientsAxios;

interface TokenContextType {
    token: string | null;
    tokenExpiry: number | null;
    tokenError: string | null; // Changed to string for detailed errors
    sessionExpired: boolean;
    setToken: (token: string | null) => void;
    setTokenExpiry: (expiry: number | null) => void;
    setTokenError: (error: string | null) => void;
    setSessionExpired: (expired: boolean) => void;
    retryTokenFetch: () => void; // Added for retry
}

const TokenContext = createContext<TokenContextType | undefined>(undefined);

export const useToken = () => {
    const context = useContext(TokenContext);
    if (!context) throw new Error('useToken must be used within TokenProvider');
    return context;
};

let globalSetSessionExpired: (expired: boolean) => void = () => { };

interface TokenProviderProps {
    children: ReactNode;
}

const TokenProvider = ({ children }: TokenProviderProps) => {
    const [token, setToken] = useState<string | null>(null);
    const [tokenExpiry, setTokenExpiry] = useState<number | null>(null);
    const [tokenError, setTokenError] = useState<string | null>(null);
    const [sessionExpired, setSessionExpired] = useState(false);
    const [retryTrigger, setRetryTrigger] = useState(0); // For retry mechanism
    const [isLoading, setIsLoading] = useState(true);

    const fetchToken = async () => {
        const existingToken = localStorage.getItem('auth_token');
        const tokenExpiry = localStorage.getItem('token_expiry');
        const now = Date.now();

        if (existingToken && tokenExpiry && now < parseInt(tokenExpiry)) {
            setToken(existingToken);
            setTokenExpiry(parseInt(tokenExpiry));
            setTokenError(null);
            setSessionExpired(false);
            return;
        }

        try {
            const { username, password } = getAuthCredentials();
            const payload: TokenRequest = { username, password };
            const formData = new FormData();
            formData.append('username', username);
            formData.append('password', password);
            const { code, data, message } = await tokenService(formData);
            
            if (code === 200 && data?.access_token) {
                const newToken = `Bearer ${data.access_token}`;
                const expiry = now + 3600 * 1000; // 60 minutes
                localStorage.setItem('auth_token', newToken);
                localStorage.setItem('token_expiry', expiry.toString());
                setToken(newToken);
                setTokenExpiry(expiry);
                setTokenError(null);
                setSessionExpired(false);
            } else {
                const errorMsg = 'Invalid response: No access token received';
                console.error(errorMsg, message);
                setTokenError(errorMsg);
            }
        } catch (error: any) {
            const errorMsg = error.response
                ? `Token fetch failed: ${error.status} ${error.data?.message || 'Unknown error'}`
                : `Network error: ${error.message}`;
            console.error('Failed to fetch token:', errorMsg);
            setTokenError(errorMsg);
            
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchToken();
    }, [retryTrigger]);

    useEffect(() => {
        globalSetSessionExpired = setSessionExpired;
    }, []);

    const retryTokenFetch = () => {
        setTokenError(null);
        setRetryTrigger((prev) => prev + 1); // Trigger useEffect
    };

    // if (isLoading) {
    //     return (
    //         <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
    //             <LoaderText /> 
    //         </Box>
    //     );
    // }

    if (tokenError) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', p: 4 }}>
                <Typography variant="h6" color="error" sx={{ mb: 2 }}>
                    {tokenError}
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={retryTokenFetch}
                    sx={{ textTransform: 'none', px: 4, py: 1.5, borderRadius: 1 }}
                >
                    Try Again
                </Button>
            </Box>
        );
    }

    return (
        <TokenContext.Provider
            value={{ token, tokenExpiry, tokenError, sessionExpired, setToken, setTokenExpiry, setTokenError, setSessionExpired, retryTokenFetch }}
        >
            {children}
        </TokenContext.Provider>
    );
};

export default TokenProvider;
export { globalSetSessionExpired };